package com.neurobs.ccabserver.administrator.controller

import com.neurobs.ccab.CCABServer
import com.neurobs.js.ccab.{CCABServer => LegacyServer}
import com.neurobs.js.dom.{ElementFactory, InputTextBox}
import com.neurobs.js.dom.ElementFactory._
import com.neurobs.js.view.{Form, Gui}

import scala.concurrent.{Future, Promise}
import scala.concurrent.ExecutionContext.Implicits.global
import scala.collection.mutable

/** Retrieves and caches data passwords
 *
 * @param userAccountId The account id of the logged in user
 */
class DataPasswordCache(userAccountId: Long, server: CCABServer) {
	private val cache = mutable.Map[Long, String]()

	def storedValue(accountId: Long): Option[String] = cache.get(accountId)

	def setValue(accountId: Long, password: String): Unit = {
		cache += (accountId -> password)
	}

	/** Gets the password, prompting the user if not cached already
	 *
	 * @param gui GUI for displaying the dialog
	 * @return Validated password, or None if cancelled
	 */
	def get(accountId: Long, gui: Gui): Future[Option[String]] = {
		storedValue(accountId) match {
			case Some(p) => Future.successful(Some(p))
			case None =>
				server.dataPasswordIsRequired(accountId)._2 flatMap {
					case Left(_) => Future.successful(None)
					case Right(required) =>
						if (!required) {
							cache += (accountId -> "deprecated")
							Future.successful(Some("deprecated"))
						}
						else DataPasswordCache.promptForPassword(accountId, gui, userAccountId).map {
							case Some(p) =>
								cache += (accountId -> p)
								Some(p)
							case None => None
						}
				}
		}
	}
}

object DataPasswordCache {
	/** Prompts the user for the data password and checks it
	 *
	 * @param accountId The account needing the password
	 * @param gui GUI for showing the dialog
	 * @param userAccountId The account id of the logged in user
	 * @return password if entered and validated, or None otherwise (including cancellation)
	 */
	private def promptForPassword(accountId: Long, gui: Gui, userAccountId: Long): Future[Option[String]] = {
		val result = Promise[Option[String]]

		val password = InputTextBox(ElementFactory.makeTextInput(width = 20))
		val form = ElementFactory.makeForm(
			width = 40,
			title = "Data Password",
			description = if (accountId == userAccountId) "Please enter your account's data password." else s"Please enter the data password for account $accountId.",
			elements = Array(("Password:", password.text)),
			useCancel = true,
			submitCaption = "Ok"
		)

		def process(callback: Option[String] => Unit): Either[String, Form.Process] = {
			if (password.value().isEmpty) Left("Please enter a password.")
			else {
				Right(LegacyServer.checkDataPassword(accountId, password.value())(LegacyServer.generalResultHandler(callback) { () =>
					result.success(Some(password.value()))
				}))
			}
		}

		gui.showModalForm(form, process, processCancellable = true, formCancel = Some(() => result.success(None)))

		result.future
	}
}
